import _ from 'lodash';
import { anyNonNil } from 'is-uuid';

import { getId } from '@nerdwallet/marketplace/selectors/common';
import { MarketplacePayloadBuilder } from '@nerdwallet/nw-api-sdk/marketplace';

import hardCodedData from './hard-coded-data';
import { FIELDS, SORT_STRATEGIES } from './constants';

const getSortStrategies = (sortKey) => SORT_STRATEGIES[sortKey] || [];

/**
 * Coerce Banking filters & parameteres into a Marketplace query payload.
 *
 * @param {Object} filters
 * @param {String?} filters.bankType
 * @param {Number?} filters.depositMinimum
 * @param {String?} filters.displayPage
 * @param {Array<String>?} filters.ids
 * @param {Boolean} filters.includeNonMonetizing
 * @param {Number?} filters.lengthOfTerm
 * @param {Number?} filters.minBankRating
 * @param {String?} filters.sortKey
 * @param {String?} filters.subProductType
 * @returns {Object} - The Marketplace query payload.
 */
export const buildMarketplaceQueryPayloadFromFilters = ({
  bankType,
  depositMinimum,
  displayPage,
  ids,
  includeNonMonetizing = true,
  lengthOfTerm,
  minBankRating,
  sortKey,
  subProductType,
}) => {
  const filteredIds = _.filter(ids, anyNonNil);
  const isValidSortStrategy = sortKey in SORT_STRATEGIES;
  const payloadBuilder = new MarketplacePayloadBuilder();

  if (isValidSortStrategy) {
    _.forEach(
      getSortStrategies(sortKey),
      /**
       * @todo - Bind `this` context to `MarketplacePayloadBuilder` instance for `MarketplacePayloadBuilder` methods.
       * @see {@link https://github.com/NerdWallet/js-nw-api-sdk/blob/dc4be0314fffa70a96c428bc80f2241dc4d0785e/src/marketplace-sdk/utils.js#L10}
       */
      payloadBuilder.addSortField.bind(payloadBuilder)
    );
  }

  if (filteredIds.length) {
    payloadBuilder
      .setQueryParams({
        field: 'offer.id',
        type: 'in',
        value: filteredIds,
      })
      .setPagination({
        offset: 0,
        limit: filteredIds.length,
      });
  }

  if (lengthOfTerm) {
    payloadBuilder.setQueryParams({
      field: 'offer.details.cd_deposit_term',
      type: 'leq',
      value: lengthOfTerm,
    });
  }

  if (depositMinimum) {
    payloadBuilder.setQueryParams({
      field: 'offer.details.minimum_balance',
      type: 'leq',
      value: depositMinimum,
    });
  }

  if (displayPage) {
    payloadBuilder.setQueryParams({
      field: 'offer.details.display_pages',
      type: 'contains_one_of',
      value: [displayPage],
    });
  }

  if (bankType) {
    payloadBuilder.setQueryParams({
      field: 'offer.product.institution.details.type',
      type: 'eq',
      value: bankType,
    });
  }

  if (subProductType) {
    payloadBuilder.setQueryParams({
      field: 'offer.product.details.sub_product_type',
      type: 'eq',
      value: subProductType,
    });
  }

  if (!includeNonMonetizing) {
    payloadBuilder.setQueryParams({
      field: 'offer.derived_details.should_throttle_monetization',
      type: 'eq',
      value: false,
    });
  }

  if (minBankRating) {
    payloadBuilder.setQueryParams({
      field: 'offer.product.institution.details.nw_rating',
      type: 'geq',
      value: minBankRating,
    });
  }

  payloadBuilder.setFields(FIELDS);

  return payloadBuilder.getPayload();
};

/**
 * Augment marketplace entity withEditorialData
 * @param {Object} marketplaceEntity
 * @returns {Obejct} - The augmented marketplace entity.
 */
export const supplementHardCodedAdditionalData = (marketplaceEntity) => ({
  ..._.get(hardCodedData, [getId(marketplaceEntity)], {}),
  ...marketplaceEntity,
});

/**
 * Get hardcoded con bullets.
 * @param {Object} marketplaceEntity
 * @returns {Array<String>} - The marketing con bullets.
 */
export const getHardCodedCons = (marketplaceEntity) =>
  _.get(marketplaceEntity, 'marketing_details.cons');

/**
 * Get hardcoded pro bullets.
 * @param {Object} marketplaceEntity
 * @returns {Array<String>} - The marketing pro bullets.
 */
export const getHardCodedPros = (marketplaceEntity) =>
  _.get(marketplaceEntity, 'marketing_details.pros');
